import { useEffect, useRef, useState } from "react";
import { Pause, Play, Square } from "lucide-react";
import { fmtDur } from "@/components/common";
import { useLive } from "@/context/AppContext";
import { api, toastErr } from "@/lib/api";
import { ShiftAccountsDialog } from "@/components/work/ShiftAccountsDialog";

// The ONLY shift start/stop logic of the site: Dashboard, sidebar and phone menu all use this hook.
// "Inizia turno" never calls the server directly: it always opens the profile choice first.
export function useWorkState() {
  const [st, setSt] = useState(null);
  const [, tick] = useState(0);
  const load = () => api.get("/work/current").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => {
    load(); const t = setInterval(() => tick((x) => x + 1), 30000);
    window.addEventListener("work:changed", load);  // Dashboard panel and sidebar always show the same shift
    return () => { clearInterval(t); window.removeEventListener("work:changed", load); };
  }, []); // eslint-disable-line
  useLive(load);
  const changed = () => window.dispatchEvent(new Event("work:changed"));
  const busy = useRef(false);
  const [pending, setPending] = useState(false);
  // One click = one request: repeated clicks, Enter or a second tab never open two shifts (the server also refuses a second open shift).
  const once = (fn) => async (...args) => { if (busy.current) return; busy.current = true; setPending(true); try { await fn(...args); } finally { busy.current = false; setPending(false); } };
  const [pick, setPick] = useState(null);
  const start = () => setPick("start");
  const confirm = once(async (ids) => {
    if (!Array.isArray(ids)) return;
    try { if (pick === "change") await api.put("/work/current/accounts", { account_ids: ids }); else await api.post("/work/start", { account_ids: ids }); setPick(null); changed(); } catch (e) { toastErr(e); }
  });
  const stop = once(async () => { if (!window.confirm("Terminare il turno?")) return; try { await api.post("/work/stop", {}); changed(); } catch (e) { toastErr(e); } });
  // official pause: the waiting bot does not count a paused person as in shift
  const pause = once(async () => { try { await api.post(st?.entry?.paused_at ? "/work/resume" : "/work/pause", {}); changed(); } catch (e) { toastErr(e); } });
  return { st, entry: st?.entry, start, stop, pause, pending, pick, setPick, confirm };
}

export function ShiftDialog({ w }) {
  const change = w.pick === "change";
  return <ShiftAccountsDialog open={!!w.pick} onClose={() => w.setPick(null)} onConfirm={w.confirm} pending={w.pending}
    current={change ? w.entry?.account_ids || [] : null} title={change ? "Modifica profili del turno" : undefined} />;
}

export function ShiftPanel({ showRevenue }) {
  const w = useWorkState();
  const { st, entry, start, stop, pause, pending, setPick } = w;
  if (!st) return null;
  const mins = entry ? (Date.now() - new Date(entry.clock_in).getTime()) / 60000 : 0;
  const paused = entry?.paused_at;
  return (
    <>
    <ShiftDialog w={w} />
    <div className={`rounded-xl border p-4 mb-3 flex flex-wrap items-center gap-4 ${paused ? "border-amber-500/50 bg-amber-500/5" : entry ? "border-emerald-500/40 bg-emerald-500/5" : "border-[#232738] bg-[#12141D]"}`} data-testid="shift-panel">
      {entry ? (
        <>
          <div className="flex-1 min-w-[200px]">
            {paused ? <div className="text-xs font-bold text-amber-300 flex items-center gap-1.5" data-testid="shift-panel-paused"><Pause className="h-3 w-3" />IN PAUSA · {fmtDur((Date.now() - new Date(paused).getTime()) / 60000)}</div>
              : <div className="text-xs font-bold text-emerald-400 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400 pulse-dot" />IN TURNO</div>}
            <div className="text-sm text-slate-300 mt-1" data-testid="shift-panel-info">Inizio <span className="num text-white">{new Date(entry.clock_in).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</span> · Durata <span className="num text-white">{fmtDur(mins)}</span>
              {showRevenue && <> · Fatturato turno <span className="num text-emerald-400 font-semibold" data-testid="shift-panel-revenue">{(entry.revenue_total || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" })}</span></>}</div>
          </div>
          <button onClick={() => setPick("change")} className="rounded-lg border border-[#232738] px-3 py-2 text-xs font-semibold text-slate-300 hover:bg-white/5" data-testid="shift-change-accounts">Modifica profili ({(entry.account_ids || []).length})</button>
          <button onClick={pause} disabled={pending} className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-bold disabled:opacity-60 ${paused ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "border border-amber-500/50 text-amber-300 hover:bg-amber-500/10"}`} data-testid={paused ? "shift-panel-resume" : "shift-panel-pause"}>{paused ? <><Play className="h-4 w-4" />RIPRENDI</> : <><Pause className="h-4 w-4" />PAUSA</>}</button>
          <button onClick={stop} disabled={pending} className="flex items-center gap-2 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-60 px-6 py-3 text-sm font-bold text-white" data-testid="shift-panel-stop"><Square className="h-4 w-4" />FINE TURNO</button>
        </>
      ) : (
        <>
          <div className="flex-1 min-w-[200px] text-sm text-slate-400">Non sei in turno. L'orario di inizio viene registrato dal server.</div>
          <button onClick={start} disabled={pending} className="flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 px-6 py-3 text-sm font-bold text-white" data-testid="shift-panel-start"><Play className="h-4 w-4" />INIZIA TURNO</button>
        </>
      )}
    </div>
    </>
  );
}
